/**
 * Product surface — the one switch that separates the INTERNAL build (the
 * "see everything" demo/dev cockpit) from a PRODUCT build (exactly what ships
 * to a client). See ProjectTasks #115.
 *
 *   internal  → the demo deployment + dev work: every module, all personas /
 *               companies / app-modes, dev tools (Mode Switcher, API Explorer,
 *               Demo Library, dev Config), and the "Preview as <client>" control
 *               (P2). This is the default, so the existing demo is UNCHANGED.
 *   product   → a client clone: their brand + modules + data + real auth, with
 *               NONE of the cockpit. A clone build sets VITE_SURFACE=product.
 *
 * Gating rule: anything that is a building/demoing aid (not part of the client's
 * actual product) must render only when `isInternal()`. Centralizing it here
 * means there is ONE place that defines "is this the internal cockpit", instead
 * of the scattered `appMode==='demo'` checks that miss dev tools.
 *
 * This is intentionally separate from:
 *   - `appMode` (demo/sandbox/dev/quality/production) — runtime data/auth posture
 *   - the Active Organization layer (#116) — which ORG's data shows in production
 *   - `VITE_MODULES` / appIdentity — which modules ship + branding per build
 * Surface answers a different question: "is the dev cockpit visible at all?"
 */

import { isPreviewingClient } from './previewClient'

export type ProductSurface = 'internal' | 'product'

/**
 * Resolved at build time from VITE_SURFACE. Unset → 'internal' so the demo
 * build and every existing deployment behave exactly as before; only a client
 * clone opts into 'product'.
 */
export const SURFACE: ProductSurface =
  (import.meta.env as Record<string, string | undefined>).VITE_SURFACE === 'product'
    ? 'product'
    : 'internal'

/**
 * True only on the internal BUILD, regardless of any live preview. Gate the
 * "Preview as <client>" control itself on THIS (so it survives preview and you
 * can exit). Everything else should gate on `isInternalSurface()`.
 */
export function isInternalBuild(): boolean {
  return SURFACE === 'internal'
}

/**
 * True on the internal/dev/demo cockpit — gate ALL cockpit chrome on this.
 * Now also false while a client preview is active (#115 P2): every gate that
 * already checks this hides during preview, so the prospect sees their clean
 * product with no dev chrome — no per-gate edits needed.
 */
export function isInternalSurface(): boolean {
  return SURFACE === 'internal' && !isPreviewingClient()
}

/** True on a client product build (a clone) OR while previewing a client. */
export function isProductSurface(): boolean {
  return SURFACE === 'product' || isPreviewingClient()
}
